import ClientChangeLogResource, { ChangeType, type ClientChangeLogType } from 'App/babel/resources/ClientChangeLog';
import { Icon, Flex, Text, Card } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import BemClass from '@upsales/components/Utils/bemClass';
import RequestBuilder from 'Resources/RequestBuilder';
import { ChangeTypeFilter, DateFilter, UserFilter } from './Filters';
import ChangeLog, { getSourceString, type ChangeLogEntry } from 'App/components/ChangeLog';
import React, { useState } from 'react';
import moment from 'moment';
import { getJourneyStep } from 'Components/Helpers/journeyStep';
import AppService from 'App/legacy/AppService';

import './ClientChangeLog.scss';

const getUser = (userId: number) => {
	const users = AppService.getUsers(undefined, true);
	return users.find((u: any) => u.id === userId);
};

export const getChangeString = (type: ChangeType, typeExtra?: string) => {
	if (type === ChangeType.ADDRESS_CHANGED) {
		// For filter, use generic address translation
		// For change rows, use specific address type translation
		if (typeExtra) {
			const suffix = typeExtra?.toLowerCase() === 'mail' ? 'post' : typeExtra?.toLowerCase();
			return `tag.client.address.${suffix}`;
		}
		return 'auditLog.type.ADDRESS_CHANGED';
	}

	switch (type) {
		case ChangeType.NAME_CHANGED:
			return 'default.name';
		case ChangeType.NOTES_CHANGED:
			return 'default.notes';
		case ChangeType.PHONE_CHANGED:
			return 'default.phone';
		case ChangeType.FAX_CHANGED:
			return 'default.fax';
		case ChangeType.WEBPAGE_CHANGED:
			return 'default.webpage';
		case ChangeType.ORG_NO_CHANGED:
			return 'default.orgNo';
		case ChangeType.COMPANY_FORM_CHANGED:
			return 'soliditet.corporateForm';
		case ChangeType.SNI_CODE_CHANGED:
			return 'default.companyBranch.sni';
		case ChangeType.PARENT_CHANGED:
			return 'default.parentCompany';
		case ChangeType.OPERATIONAL_ACCOUNT_CHANGED:
			return 'auditLog.type.OPERATIONAL_ACCOUNT_CHANGED';
		case ChangeType.ACTIVE_CHANGED:
			return 'default.active';
		case ChangeType.IS_MONITORED_CHANGED:
			return 'auditLog.type.IS_MONITORED_CHANGED';
		case ChangeType.DUNS_NO_CHANGED:
			return 'default.duns';
		case ChangeType.CFAR_CHANGED:
			return 'default.cfar';
		case ChangeType.CREDIT_RATING_CHANGED:
			return 'default.creditRating';
		case ChangeType.PROSPECTING_CREDIT_RATING_CHANGED:
			return 'admin.fields.prospectingCreditRating';
		case ChangeType.TURNOVER_CHANGED:
			return 'default.turnover';
		case ChangeType.PROFIT_CHANGED:
			return 'default.profit';
		case ChangeType.NO_EMPLOYEES_CHANGED:
			return 'default.noEmployees';
		case ChangeType.FACEBOOK_CHANGED:
			return 'default.facebook';
		case ChangeType.TWITTER_CHANGED:
			return 'default.twitter';
		case ChangeType.LINKEDIN_CHANGED:
			return 'default.linkedin';
		case ChangeType.SIC_CODE_CHANGED:
			return 'default.companyBranch.sic';
		case ChangeType.NAICS_CODE_CHANGED:
			return 'default.companyBranch.naics';
		case ChangeType.UK_SIC_CODE_CHANGED:
			return 'default.companyBranch.uksic';
		case ChangeType.NACE_CODE_CHANGED:
			return 'default.companyBranch.nace';
		case ChangeType.ABOUT_CHANGED:
			return 'default.about';
		case ChangeType.JOURNEY_STEP_CHANGED:
			return 'default.journeyStep';
		case ChangeType.PRICE_LIST_CHANGED:
			return 'default.priceList';
		case ChangeType.ACCOUNT_MANAGER_CHANGED:
			return 'default.accountManager';
		case ChangeType.CATEGORIES_CHANGED:
			return 'default.categories';
		case ChangeType.CAMPAIGNS_CHANGED:
			return 'default.campaigns';
		case ChangeType.CUSTOM_FIELD_CHANGED:
			return 'auditLog.type.CUSTOM_FIELD_CHANGED';
		default:
			return 'Unknown';
	}
};

const formatValue = (type: ChangeType, value: any, t: any) => {
	switch (type) {
		case ChangeType.PARENT_CHANGED:
		case ChangeType.OPERATIONAL_ACCOUNT_CHANGED:
		case ChangeType.PRICE_LIST_CHANGED:
			return value?.name ?? null;

		case ChangeType.ACTIVE_CHANGED:
		case ChangeType.IS_MONITORED_CHANGED:
			return value != null ? (value ? t('default.yes') : t('default.no')) : null;

		case ChangeType.JOURNEY_STEP_CHANGED: {
			if (!value) {
				return null;
			}
			const journeyStep = getJourneyStep(value);
			if (!journeyStep) {
				return value;
			}
			return t(journeyStep.name);
		}

		case ChangeType.ACCOUNT_MANAGER_CHANGED:
			// value is {accountManager: [{id: number, name: string}, ...]}
			if (value?.accountManager && Array.isArray(value.accountManager)) {
				return value.accountManager.map((am: any) => am.name).join(', ');
			}
			return null;

		case ChangeType.CATEGORIES_CHANGED:
			// value is {categories: [{id: number, name: string}, ...]}
			if (value?.categories && Array.isArray(value.categories)) {
				return value.categories.map((cat: any) => cat.name).join(', ');
			}
			return null;

		case ChangeType.CAMPAIGNS_CHANGED:
			// value is {project: [{id: number, name: string}, ...]}
			if (value?.project && Array.isArray(value.project)) {
				return value.project.map((campaign: any) => campaign.name).join(', ');
			}
			return null;

		case ChangeType.CUSTOM_FIELD_CHANGED:
			if (!value || typeof value !== 'object') {
				return null;
			}
			switch (value?.datatype) {
				case 'Boolean':
					return ['on', true, 'true', 1, '1'].includes(value?.value) ? t('default.yes') : t('default.no');
				case 'Date':
					return value?.value ? moment(value?.value).format('L') : null;
				case 'Percent':
					return value?.value != null && value?.value !== '' ? `${value?.value}%` : null;
				case 'User':
					return value?.value ? getUser(parseInt(value?.value))?.name : null;
				case 'Users':
					return value?.value
						? value.value
								.split(',')
								.map((user: any) => getUser(parseInt(user))?.name)
								.join(', ')
						: null;
				default:
					return value?.value ?? null;
			}

		default:
			return value ?? null;
	}
};

const RowChanged = ({
	type,
	oldValue,
	newValue,
	typeExtra
}: {
	type: ChangeType;
	oldValue: any;
	newValue: any;
	typeExtra?: string;
}) => {
	const classes = new BemClass('ClientChangeLog');
	const { t } = useTranslation();

	if (type === ChangeType.ADDRESS_CHANGED) {
		const addressType = typeExtra || 'Mail';

		const fieldNames: Record<string, string> = {
			address: 'address.address',
			zipcode: 'address.zip',
			city: 'address.city',
			state: 'address.state',
			municipality: 'address.municipality',
			country: 'address.country'
		};

		// Get all fields that have changed (present in either oldValue or newValue)
		const allFields = new Set<string>();
		[oldValue, newValue].forEach(value => {
			if (value && typeof value === 'object') {
				Object.keys(value).forEach(field => {
					if (field !== 'addressType') {
						allFields.add(field);
					}
				});
			}
		});

		const fieldChanges = Array.from(allFields).map(field => ({
			field,
			fieldName: t(fieldNames[field] || field),
			oldVal: oldValue?.[field],
			newVal: newValue?.[field]
		}));

		if (fieldChanges.length === 0) {
			return (
				<Flex className={classes.elem('change').b()} direction="column" space="pll prl ptm pbm" gap="u1">
					<Text size="sm" color="grey-11">
						{t(getChangeString(type, addressType))}
					</Text>
				</Flex>
			);
		}

		return (
			<Flex className={classes.elem('change').b()} direction="column" space="pll prl ptm pbm" gap="u1">
				<Text size="sm" color="grey-11">
					{t(getChangeString(type, addressType))}
				</Text>
				{fieldChanges.map(({ field, fieldName, oldVal, newVal }) => (
					<Flex key={field} gap="u1" alignItems="baseline">
						<Text size="sm" color="grey-11" italic>
							{fieldName}
							{': '}
							{oldVal != null && oldVal !== '' ? oldVal : t('default.none')}
						</Text>
						<Icon name="arrow-right" className={classes.elem('arrow-right').b()} />
						<Text size="sm">
							{fieldName}
							{': '}
							{newVal != null && newVal !== '' ? newVal : t('default.none')}
						</Text>
					</Flex>
				))}
			</Flex>
		);
	}

	const formattedOldValue = formatValue(type, oldValue, t);
	const formattedNewValue = formatValue(type, newValue, t);

	const isCustomFieldChange = type === ChangeType.CUSTOM_FIELD_CHANGED;

	return (
		<Flex className={classes.elem('change').b()} direction="column" space="pll prl ptm pbm" gap="u1">
			<Text size="sm" color="grey-11">
				{isCustomFieldChange ? newValue?.name || oldValue?.name : t(getChangeString(type))}
			</Text>
			<Flex gap="u1" alignItems="baseline">
				{formattedOldValue != null && formattedOldValue !== '' ? (
					<Text color="grey-11" italic>
						{formattedOldValue}
					</Text>
				) : (
					<Text color="grey-11" italic>
						{isCustomFieldChange && newValue?.datatype === 'Boolean' ? t('default.no') : t('default.none')}
					</Text>
				)}
				<Icon name="arrow-right" className={classes.elem('arrow-right').b()} />
				{formattedNewValue != null && formattedNewValue !== '' ? (
					<Text>{formattedNewValue}</Text>
				) : (
					<Text color="grey-11" italic>
						{t('default.none')}
					</Text>
				)}
			</Flex>
		</Flex>
	);
};

const ClientChanged = ({ change }: { change: ClientChangeLogType }) => {
	return (
		<>
			{change.changes.map((row, index) => (
				<RowChanged
					key={index}
					type={row.type}
					oldValue={row.oldValue}
					newValue={row.newValue}
					typeExtra={row.typeExtra}
				/>
			))}
		</>
	);
};

const LIMIT = 10;

const fetcher = (
	rb: RequestBuilder,
	{
		clientId,
		userFilter,
		changeTypeFilter,
		dateFilter
	}: { clientId: number; userFilter: number[]; changeTypeFilter: string[]; dateFilter: { start?: any; end?: any } }
) => {
	if (userFilter.length > 0) {
		rb.addFilter({ field: 'modBy' }, 'eq', userFilter);
	}
	if (changeTypeFilter.length > 0) {
		rb.addFilter({ field: 'type' }, 'eq', changeTypeFilter);
	}
	if (dateFilter.start) {
		rb.addFilter({ field: 'modDate' }, 'gte', dateFilter.start);
	}
	if (dateFilter.end) {
		rb.addFilter({ field: 'modDate' }, 'lte', dateFilter.end);
	}
	return ClientChangeLogResource.getClientChangeLog(clientId, rb.limit ?? LIMIT, rb.offset ?? 0, rb.build());
};

const ClientChangeLog = ({ clientId }: { clientId: number }) => {
	const { t } = useTranslation();

	const [userFilter, setUserFilter] = useState<number[]>([]);
	const [dateFilter, setDateFilter] = useState<{ start?: any; end?: any }>({});
	const [changeTypeFilter, setChangeTypeFilter] = useState<string[]>([]);

	const classes = new BemClass('ClientChangeLog');

	return (
		<Card className={classes.elem('card').b()} spacing="pl4 pr4 pt4">
			<ChangeLog<ClientChangeLogType>
				className="ClientChangeLog"
				title={t('history')}
				fetcher={(rb, props) => fetcher(rb, props)}
				fetcherProps={{ clientId, userFilter, changeTypeFilter, dateFilter }}
				limit={LIMIT}
				getSourceString={(source, translateFn) => getSourceString(source, translateFn) ?? null}
				renderChangeDetails={(change: ChangeLogEntry<ClientChangeLogType>) => {
					return <ClientChanged change={change as unknown as ClientChangeLogType} />;
				}}
				filters={
					<>
						<DateFilter filter={dateFilter} onChange={setDateFilter} />
						<UserFilter filter={userFilter} onChange={setUserFilter} />
						<ChangeTypeFilter filter={changeTypeFilter} onChange={setChangeTypeFilter} />
					</>
				}
			/>
		</Card>
	);
};

export default ClientChangeLog;
